/**
 * The top-right inspector for IFC models: Structure (search, group-by, tree)
 * and Properties (the selected element). Same slot and look as DamagePopup.
 */
import { Building2, X } from 'lucide-react';
import type { MouseEvent, WheelEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIfcFeatures, type IfcPanelTab } from '@/context/IfcFeatureContext';
import { cn } from '@/lib/utils';
import IfcPropertiesTab from './IfcPropertiesTab';
import IfcStructureTab from './IfcStructureTab';

const stop = (event: MouseEvent | WheelEvent) => event.stopPropagation();

export default function IfcPanel() {
	const { t } = useTranslation();
	const { panel, entries, closePanel, setPanelTab } = useIfcFeatures();
	if (!panel) return null;
	const entry = entries[panel.key];
	if (!entry) return null;

	return (
		<div
			className={cn(
				'absolute top-24 right-6 z-50 w-80 animate-in fade-in slide-in-from-right-4',
				'duration-300'
			)}
			onMouseDown={stop}
			onClick={stop}
			onWheel={stop}
		>
			<Card
				className={cn(
					'gap-0 overflow-hidden border-border bg-card/95 py-0',
					'shadow-2xl backdrop-blur-sm'
				)}
			>
				<div className="flex items-center justify-between border-b border-border bg-muted/30 p-3">
					<h3 className="flex min-w-0 items-center gap-2 text-sm font-bold text-card-foreground">
						<Building2 className="h-4 w-4 shrink-0 text-primary" />
						<span className="truncate" title={entry.name}>
							{t('viewer.ifc.title', { name: entry.name || t('viewer.ifc.untitledModel') })}
						</span>
					</h3>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
						onClick={closePanel}
						title={t('common.actions.close')}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>

				<Tabs
					value={panel.tab}
					onValueChange={(tab) => setPanelTab(tab as IfcPanelTab)}
					className="gap-0"
				>
					<TabsList className="h-9 w-full rounded-none border-b border-border bg-transparent p-0">
						<TabsTrigger value="structure" className="text-xs">
							{t('viewer.ifc.tabs.structure')}
						</TabsTrigger>
						<TabsTrigger value="properties" className="text-xs">
							{t('viewer.ifc.tabs.properties')}
						</TabsTrigger>
					</TabsList>
					<div className="max-h-[70vh] overflow-y-auto">
						<TabsContent value="structure" className="m-0">
							<IfcStructureTab key={panel.key} tilesetKey={panel.key} />
						</TabsContent>
						<TabsContent value="properties" className="m-0">
							<IfcPropertiesTab key={panel.key} tilesetKey={panel.key} />
						</TabsContent>
					</div>
				</Tabs>
			</Card>
		</div>
	);
}
